import {Component, computed, HostListener, signal, ViewEncapsulation} from "@angular/core";
import {MatIcon} from "@angular/material/icon";
import {DialogBase} from "../dialog/dialog.base";

export interface LightboxData {
    photos: string[];
    initialIndex?: number;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_MS = 300;

@Component({
    standalone: true,
    imports: [MatIcon],
    templateUrl: "./lightbox.component.html",
    styleUrl: "./lightbox.component.scss",
    encapsulation: ViewEncapsulation.None,
})
export class LightboxComponent extends DialogBase<LightboxData, void> {

    photos = this.data.photos;
    currentIndex = signal(this.clampIndex(this.data.initialIndex ?? 0));

    scale = signal(1);
    translateX = signal(0);
    translateY = signal(0);

    transform = computed(
        () => `translate(${this.translateX()}px, ${this.translateY()}px) scale(${this.scale()})`,
    );

    hasPrev = computed(() => this.currentIndex() > 0);
    hasNext = computed(() => this.currentIndex() < this.photos.length - 1);
    currentPhoto = computed(() => this.photos[this.currentIndex()]);

    private pointers = new Map<number, { x: number; y: number }>();
    private lastPanX = 0;
    private lastPanY = 0;
    private startDistance = 0;
    private startScale = 1;
    private lastTapAt = 0;

    next() {
        if (!this.hasNext()) return;
        this.currentIndex.update((i) => i + 1);
        this.resetTransform();
    }

    prev() {
        if (!this.hasPrev()) return;
        this.currentIndex.update((i) => i - 1);
        this.resetTransform();
    }

    close() {
        this.dialogRef.close();
    }

    @HostListener("window:keydown", ["$event"])
    onKeyDown(event: KeyboardEvent) {
        if (event.key === "ArrowLeft") this.prev();
        else if (event.key === "ArrowRight") this.next();
    }

    onWheel(event: WheelEvent) {
        event.preventDefault();
        const delta = -event.deltaY * 0.002;
        this.applyScale(this.scale() + delta);
    }

    onPointerDown(event: PointerEvent) {
        (event.target as Element).setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});

        if (this.pointers.size === 1) {
            const now = Date.now();
            if (now - this.lastTapAt < DOUBLE_TAP_MS) {
                this.toggleZoom();
                this.lastTapAt = 0;
            } else {
                this.lastTapAt = now;
            }
            this.lastPanX = event.clientX;
            this.lastPanY = event.clientY;
        } else if (this.pointers.size === 2) {
            const [p1, p2] = Array.from(this.pointers.values());
            this.startDistance = Math.hypot(p2.x - p1.x, p2.y - p1.y);
            this.startScale = this.scale();
        }
    }

    onPointerMove(event: PointerEvent) {
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});

        if (this.pointers.size === 2) {
            const [p1, p2] = Array.from(this.pointers.values());
            const distance = Math.hypot(p2.x - p1.x, p2.y - p1.y);
            if (this.startDistance > 0) {
                this.applyScale((distance / this.startDistance) * this.startScale);
            }
        } else if (this.pointers.size === 1) {
            const dx = event.clientX - this.lastPanX;
            const dy = event.clientY - this.lastPanY;
            this.translateX.update((x) => x + dx);
            this.translateY.update((y) => y + dy);
            this.lastPanX = event.clientX;
            this.lastPanY = event.clientY;
        }
    }

    onPointerUp(event: PointerEvent) {
        this.pointers.delete(event.pointerId);
        if (this.pointers.size < 2) this.startDistance = 0;
    }

    private toggleZoom() {
        if (this.scale() > 1) this.resetTransform();
        else this.scale.set(2);
    }

    private applyScale(next: number) {
        const clamped = Math.min(Math.max(next, MIN_SCALE), MAX_SCALE);
        this.scale.set(clamped);
    }

    private resetTransform() {
        this.scale.set(1);
        this.translateX.set(0);
        this.translateY.set(0);
    }

    private clampIndex(index: number): number {
        return Math.min(Math.max(index, 0), Math.max(this.photos.length - 1, 0));
    }
}
